import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFavorites } from '@/hooks/useFavorites';
import { formatDate, formatViews, type Movie } from '@/lib/movies';

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const movie = items[index];
  const saved = isFavorite(movie.id);

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[600px] md:h-[720px] overflow-hidden">
      <div key={movie.id} className="absolute inset-0">
        <img
          src={movie.banner ?? movie.cover}
          alt={`${movie.title} 焦点剧照`}
          title={`${movie.title} - 精选影片剧照`}
          className="w-full h-full object-cover object-top hero-zoom"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-background-50/60" />

      <div className="relative z-10 h-full w-full px-4 md:px-8 pt-24 md:pt-32 pb-20 md:pb-24 flex flex-col justify-end">
        <div className="max-w-2xl animate-fade-up">
          <h1 className="text-[11px] md:text-xs tracking-[0.3em] text-secondary-600 font-label uppercase">
            av免费网站 · 精选高清影片在线观看
          </h1>

          <div className="mt-4 flex items-center gap-3">
            <span className="h-6 px-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-[11px] tracking-[0.2em] font-label flex items-center">
              FEATURED
            </span>
            <span className="text-xs tracking-[0.24em] text-foreground-600 font-label uppercase">
              {movie.year} · {movie.region}
            </span>
          </div>

          <h2 className="mt-5 text-3xl md:text-6xl font-black leading-tight tracking-tight text-foreground-950">
            {movie.title}
          </h2>
          <p className="mt-2 text-sm md:text-base tracking-[0.18em] text-foreground-500 font-label uppercase">
            {movie.latin}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-foreground-700">
            <span className="inline-flex items-center gap-1.5 text-primary-400">
              <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-sm" />
              {movie.rating.toFixed(1)}
            </span>
            <span className="w-1 h-1 rounded-full bg-background-400" />
            <span>{movie.duration} 分钟</span>
            <span className="w-1 h-1 rounded-full bg-background-400" />
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center text-sm" />
              {formatViews(movie.views)} 次浏览
            </span>
            <span className="w-1 h-1 rounded-full bg-background-400" />
            <span>更新于 {formatDate(movie.updatedAt)}</span>
          </div>

          <p className="mt-5 max-w-xl text-sm md:text-base leading-relaxed text-foreground-700 line-clamp-3">
            {movie.synopsis}
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link
              to={`/detail/${movie.id}`}
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg w-5 h-5 flex items-center justify-center" />
              立即观看
            </Link>
            <button
              type="button"
              onClick={() => toggleFavorite(movie.id)}
              className={`inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border text-sm transition-colors cursor-pointer whitespace-nowrap ${
                saved
                  ? 'border-accent-400/60 bg-accent-500/15 text-accent-300'
                  : 'border-background-400/60 bg-background-100/60 text-foreground-800 hover:border-primary-500/60 hover:text-foreground-950'
              }`}
            >
              <i className={`${saved ? 'ri-heart-fill' : 'ri-heart-line'} text-lg w-5 h-5 flex items-center justify-center`} />
              {saved ? '已加入收藏' : '加入收藏'}
            </button>
            <Link
              to={`/category/${movie.categories.includes('classic') ? 'classic' : 'latest'}`}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md text-sm text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看同类影片
              <i className="ri-arrow-right-line text-base w-5 h-5 flex items-center justify-center" />
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 md:bottom-10 left-0 right-0 z-20 px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {items.map((item, dotIndex) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(dotIndex)}
              aria-label={`切换到 ${item.title}`}
              className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                dotIndex === index ? 'w-10 bg-primary-500' : 'w-5 bg-background-400/70 hover:bg-background-500'
              }`}
            />
          ))}
          <span className="ml-2 text-[11px] tracking-[0.24em] text-foreground-500 font-label">
            {String(index + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
          </span>
        </div>

        <div className="hidden sm:flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="上一部焦点影片"
            className="w-10 h-10 rounded-full border border-background-400/60 text-foreground-700 hover:border-primary-500/70 hover:text-foreground-950 flex items-center justify-center cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="下一部焦点影片"
            className="w-10 h-10 rounded-full border border-background-400/60 text-foreground-700 hover:border-primary-500/70 hover:text-foreground-950 flex items-center justify-center cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}